import { useState, useEffect, useRef } from "react";
import { Formik, Form, Field } from "formik";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Textarea,
  HStack,
  VStack,
  Box,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import { FaStar } from "react-icons/fa";
import confetti from "canvas-confetti";
import { createFeedback } from "@/services/feedback.service";

export default function FeedbackModal({ isOpen = false, onClose = () => {} }) {
  const toast = useToast();
  const [showThanks, setShowThanks] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const modalSize = useBreakpointValue({ base: "full", md: "md" });
  const starSize = useBreakpointValue({ base: "40px", md: "50px" }) || "40px";
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setShowThanks(false);
    }
  }, [isOpen]);

  const handleSubmit = async (
    values: { rating: number; feedback: string },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      await createFeedback({ rating: values.rating, text: values.feedback });
      setShowThanks(true);
      setTimeout(() => {
        if (modalRef.current) {
          const rect = modalRef.current.getBoundingClientRect();
          confetti({
            particleCount: 100,
            spread: 70,
            origin: {
              x: (rect.left + rect.width / 2) / window.innerWidth,
              y: (rect.top + rect.height / 2) / window.innerHeight,
            },
            zIndex: 9999,
            disableForReducedMotion: true,
          });
        }
      }, 100);
      setTimeout(() => {
        setSubmitting(false);
        onClose();
      }, 3000);
    } catch (error) {
      setSubmitting(false);
      onClose();
      toast({
        title: "Erro",
        description: "Tivemos um erro ao enviar sua avaliação",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={modalSize}>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <Formik
        initialValues={{ rating: 0, feedback: "" }}
        onSubmit={handleSubmit}
        validate={(values) => {
          const errors: { feedback?: string } = {};
          if (values.feedback.length < 100) {
            errors.feedback = "Precisa escrever pelo menos 100 letras";
          }
          return errors;
        }}
      >
        {({ values, isSubmitting, setFieldValue, errors }) => (
          <ModalContent
            ref={modalRef}
            bg={showThanks ? "green.500" : "white"}
            borderRadius={{ base: 0, md: "xl" }}
            boxShadow="xl"
            p={6}
            maxW={{ base: "100%", md: "md" }}
            h={{ base: "100vh", md: "auto" }}
          >
            <Form
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {showThanks ? (
                <VStack spacing={4} align="center" justify="center" h="100%">
                  <Text
                    fontSize={{ base: "4xl", md: "5xl" }}
                    fontWeight="bold"
                    color="white"
                    textAlign="center"
                  >
                    Obrigado 🎉
                  </Text>
                </VStack>
              ) : (
                <>
                  <ModalHeader>
                    <Text
                      fontSize={{ base: "2xl", md: "3xl" }}
                      fontWeight="bold"
                      textAlign="center"
                      color="gray.700"
                    >
                      Quantas estrelas nossa plataforma merece?
                    </Text>
                  </ModalHeader>
                  <ModalBody>
                    <VStack spacing={8}>
                      <Box w="100%">
                        <HStack spacing={4} justify="center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Box
                              key={star}
                              as={FaStar}
                              fontSize={starSize}
                              color={
                                star <= (hoverRating || values.rating)
                                  ? "yellow.400"
                                  : "gray.300"
                              }
                              cursor="pointer"
                              onClick={() => setFieldValue("rating", star)}
                              onMouseEnter={() => setHoverRating(star)}
                              onMouseLeave={() => setHoverRating(0)}
                              transition="all 0.2s"
                              _hover={{ transform: "scale(1.2)" }}
                              role="button"
                              aria-label={`Rate ${star} stars`}
                            />
                          ))}
                        </HStack>
                      </Box>
                      <Box w="100%">
                        <Text mb={2} fontWeight="medium" color="gray.600">
                          Nos conte mais sobre sua experiência:{" "}
                          <Text as="span" color="red.500">
                            *
                          </Text>
                        </Text>
                        <Field
                          as={Textarea}
                          name="feedback"
                          placeholder="Digite aqui como foi usar nossa plataforma"
                          rows={4}
                          borderRadius="md"
                          borderColor="gray.300"
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "outline",
                          }}
                          resize="none"
                        />
                        {errors.feedback && (
                          <Text color="red.500" fontSize="sm">
                            {errors.feedback}
                          </Text>
                        )}
                      </Box>
                    </VStack>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      type="submit"
                      colorScheme="secondary"
                      size="lg"
                      borderRadius="full"
                      px={8}
                      fontWeight="bold"
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "lg",
                      }}
                      transition="all 0.2s"
                      isDisabled={
                        values.rating === 0 ||
                        values.feedback.length < 100 ||
                        isSubmitting
                      }
                      _disabled={{
                        backgroundColor: "gray.300",
                        cursor: "not-allowed",
                        _hover: { transform: "none", boxShadow: "none" },
                      }}
                    >
                      Enviar para nosso time
                    </Button>
                  </ModalFooter>
                </>
              )}
            </Form>
          </ModalContent>
        )}
      </Formik>
    </Modal>
  );
}
